import { Button } from "@material-ui/core";
import { ArrowDropDown, ArrowDropUp, Remove } from "@material-ui/icons";
import { CommandEletronicExtract } from "Infrastructure/repositories/api/ExtractRepository";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  OrientationType,
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import Toolbar from "areas/_catalog/components/toolbar/Toolbar";
import { moneyMask } from "corss-cutting/masks/money";
import React, { FC, useCallback, useMemo } from "react";
import { hideCnpj, hideCpf, toDateAndTime, validateCNPJ, validateCPF } from "utils/utils";
import styles from "./PrepaidExtract.module.scss";
import { useTranslation } from "i18n/useTranslation";

interface IPrepaidExtract {
  extract: CommandEletronicExtract;
  onClickAdd: () => void;
}

export const PrepaidExtract: FC<IPrepaidExtract> = ({
  extract,
  onClickAdd,
}) => {
  const { kiosk } = useKiosk();
  const { t } = useTranslation();
  const { resolution, orientation } = useScreenStore();

  const renderDocument = useCallback(
    (document: string) => {
      if(validateCPF(document)) {
        return hideCpf(document);
      }

      if (validateCNPJ(document)) {
        return hideCnpj(document);
      }

      return document;
    },
    [],
  )
  

  const showAddCreditButton = useMemo(
    () =>
      kiosk?.paymentVoucher ||
      kiosk?.paymentCredit ||
      kiosk?.paymentDebit ||
      kiosk?.paymentPix,
    [kiosk]
  );

  return (
    <div
      id={styles.PrepaidExtract}
      style={{
        marginTop: resolution === ResolutionType.FULL_HD ? "0" : "-100px",
      }}
    >
      <Toolbar hideWelcome title={kiosk?.localName ?? ""} />

      <div className={styles.user}>
        <p
          style={{ fontSize: resolution === ResolutionType.FULL_HD ? 35 : 25 }}
        >
          {extract.user?.name}
        </p>
        <p
          style={{ fontSize: resolution === ResolutionType.FULL_HD ? 25 : 18 }}
        >
          {renderDocument(extract.user?.document ?? "")}
        </p>
      </div>

      <div className={styles.content}>
        <div 
          style={{
            maxHeight: orientation === OrientationType.PORTRAIT ? resolution === ResolutionType.FULL_HD ? 1400 : 830 : 550,
          }}
        >
          {extract.orders.map((it, key) => (
            <div key={key} className={styles.item}>
              <div>
                <span>{toDateAndTime(String(it.createdAt))}</span>
                <span>
                  {it.items.reduce(
                    (acc, curr) =>
                      acc +
                      (!curr.isCanceled && !curr.isTransfered
                        ? curr.totalValue
                        : 0),
                    0
                  ) === 0 ? (
                    <Remove style={{ color: "#a2a2a2" }} />
                  ) : !it.isPayment ? (
                    <ArrowDropUp style={{ color: "#C00D1E" }} />
                  ) : (
                    <ArrowDropDown style={{ color: "#399C54" }} />
                  )}
                </span>
              </div>
              <>
                {it.items.map((item, _key) => (
                  <div
                    key={_key}
                    style={{
                      textDecoration: item.isCanceled
                        ? "line-through"
                        : undefined,
                      color: item.isCanceled
                        ? "#ED0029"
                        : item.isTransfered
                        ? "#a2a2a2"
                        : undefined,
                    }}
                  >
                    <span>{`${item.quantity}x ${item.name} ${
                      item.isTransfered ? t("cashless.transferred") : ""
                    }`}</span>
                    <span>{`${10 < 0 ? "- " : ""}${moneyMask(
                      item.totalValue.toFixed(2)
                    )}`}</span>
                  </div>
                ))}
                <div>
                  <span>
                    <b>{t("cashless.total")}</b>
                  </span>
                  <span>
                    <b>
                      {moneyMask(
                        it.items
                          .reduce(
                            (acc, curr) =>
                              acc +
                              (!curr.isCanceled && !curr.isTransfered
                                ? curr.totalValue
                                : 0),
                            0
                          )
                          .toFixed(2)
                      )}
                    </b>
                  </span>
                </div>
              </>
            </div>
          ))}
        </div>

        <div>
          <div className={styles.footer}>
            <div>
              <span>
                <b>{t("cashless.balance")}</b>
              </span>
              <span style={{ color: "#399C54" }}>
                <b>{moneyMask(extract.balance.toFixed(2))}</b>
              </span>
            </div>
            {showAddCreditButton && (
              <Button
                variant="contained"
                fullWidth
                onClick={onClickAdd}
                style={{ backgroundColor: "#1a5" }}
              >
                {t("cashless.add_credit")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
